import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output
} from '@angular/core';
import { AbstractControl, FormsModule, ReactiveFormsModule, UntypedFormGroup } from '@angular/forms';

import { combineLatest, Observable, of } from 'rxjs';
import { map, startWith, takeUntil, tap } from 'rxjs/operators';

import { CoreObject } from '@summit/core';
import { DisplaySchema, SummitDisposing, SummitIdObject, ViewField } from '@summit/models';
import { CrudCacheParameters } from '@summit/services';
import { ApiError, BlurDirective } from '@summit/ui';

import { FloatLabelModule } from 'primeng/floatlabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputText } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { SFormErrorInputComponent } from '../s-form-error-input';

@Component({
  selector: 's-form-input-select',
  templateUrl: './s-form-input-select.component.html',
  styleUrls: ['./s-form-input-select.component.scss'],
  standalone: true,
  imports: [
    InputText,
    InputGroupModule,
    FloatLabelModule,
    SelectModule,
    SFormErrorInputComponent,
    AsyncPipe,
    ReactiveFormsModule,
    FormsModule,
    BlurDirective
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SFormInputSelectComponent<T extends SummitIdObject> extends SummitDisposing implements OnInit {
  @Input() label: string;
  @Input() form: UntypedFormGroup;
  @Input() groupName: string;
  @Input() fieldName: (keyof T & string) | string;
  @Input() apiError: ApiError;
  @Input() schema: DisplaySchema<T> | false;
  @Input() defaultText: string;
  @Input() filter: string[] | number[];
  @Input() filter$: Observable<(string | number)[]> = of([]);
  @Input() nonFormValue: unknown;
  @Input() disabled = false;
  // if deselect is true, then entry will not automatically select the first entry
  @Input() deselect = false;
  @Input() required: boolean;
  @Input() readOnly = false;
  @Input() items$: Observable<any[]>;
  @Input() cacheParameters: Observable<CrudCacheParameters>;
  @Input() allowZero = false;
  @Input() displayHint: string;
  @Input() showClear = false;
  @Input() readonly = false;
  @Input() enum = false;
  @Input() displayProperty: string;
  @Output() triggerChange: EventEmitter<any> = new EventEmitter<any>();

  private currentValue: any[];

  //@ViewChild('control') dropdownComponent;
  // this.dropdownComponent.focusViewChild.nativeElement.focus();
  // this.dropdownComponent.applyFocus()
  myControl: AbstractControl | null;

  attribute: ViewField<T>;
  id: string;
  public displayItems$: Observable<any[]>;
  public isRequired: boolean;

  constructor(private cd: ChangeDetectorRef) {
    super();
  }

  get itemsCache() {
    return this.currentValue;
  }

  ngOnInit() {
    //this.displayItems$ = this.items?.asObservable();

    //console.log(this.id, typeof this.id);
    this.id = this.getID();
    this.displayProperty ??= this.enum ? 'value' : 'name';

    if (this.schema) {
      this.attribute = this.schema.getAttribute(this.fieldName as keyof T & string, this.groupName);
      this.isRequired = this.required ?? this._isRequired();
      if (!this.attribute) {
        console.error(
          `Could not find Property ${this.fieldName?.toString()} on branch ${this.groupName} on tree:`,
          this.schema
        );
      } else {
        // console.log(`Selecting between ${this.label} and ${this.property.displayName}`);
        this.label = this.label ? this.label : this.attribute.displayName;
        this.defaultText = this.defaultText ? this.defaultText : `Select an ${this.label}`;
      }
    } else {
      if (this.schema != false) console.error(`No Schema provided`);
      this.isRequired = this.required;
    }
    if (this.items$) {
      //console.log("filter$", this.filter$)
      this.displayItems$ = combineLatest([
        this.items$?.pipe(startWith([])) ?? of([]),
        this.filter$?.pipe(startWith([])) ?? of([])
        //this.myControl?.valueChanges.pipe(startWith(this.myControl?.value)) ?? of([])
      ]).pipe(
        tap(([entries, _filterIds]) => {
          this.currentValue = entries;
          //console.log('value', value);
        }),
        map(([entries, filterIds]) =>
          entries.filter(
            (entry) =>
              entry &&
              (!filterIds || filterIds.findIndex((filter) => filter == entry.id) == -1) &&
              (!this.filter || this.filter.findIndex((filter) => filter == entry.id) == -1)
          )
        ),
        tap((x) => {
          if (x.length > 0) {
            const currentItem = x.find((entry) => entry.id == this.myControl?.value);
            if (this.fieldName == 'incentiveId')
              console.log(`${this.fieldName.toString()} currentItem`, currentItem, this.myControl?.value);
            if (currentItem && !(currentItem instanceof CoreObject)) {
              // if (this.fieldName == 'companyId')
              //   console.log(
              //     `Setting ${this.fieldName.toString()} to ${this.myControl?.value}`,
              //     this.myControl?.value,
              //     x
              //   );
              this.myControl?.patchValue(this.myControl?.value);
            }
          }
        })
      );
      this.displayItems$.pipe(takeUntil(this.disposing$)).subscribe((x) => {
        //if (this.fieldName == null) console.log('displayed Items:', x);
        if (x?.length === 1 && this.isRequired && !(x instanceof CoreObject)) {
          //console.log(`Single Entry: Setting ${this.fieldName?.toString()} to ${x[0].id}`, x);
          this.form?.controls[this.fieldName?.toString()]?.patchValue(x[0].id);
          this.cd.markForCheck();
        }
      });
    } else {
      console.error(`No Items were provided`);
    }
    if (this.disabled) {
      this.form?.controls[this.fieldName?.toString()].disable();
    }
    //console.log(`${this.fieldName.toString()}: this.form`, this.form.controls[this.fieldName.toString()]);
  }

  getID(): string {
    let response = '';
    if (this.groupName) {
      response += this.groupName + '_';
    }
    response += this.fieldName?.toString();
    return response;
  }

  getValue(): any {
    return this.nonFormValue ?? this.form?.controls[this.fieldName?.toString()]?.value;
  }
  getItem(): string {
    const tempValue = this.getValue();
    const item = this.currentValue?.find((entry) => entry.id === tempValue);
    return item?.toShortString ? item.toShortString() : item?.value;
  }

  getObject(): any {
    const tempValue = this.getValue();
    return this.currentValue?.find((entry) => entry.id === tempValue);
  }

  onChange(): void {
    //console.log(`Selecting ${this.getValue()} for ${this.fieldName?.toString()}`);
    this.triggerChange.emit(this.getValue());
  }

  setValue(id: string | number) {
    //this.control.writeValue(id);
    //console.log(`Setting ${this.fieldName?.toString()} to ${id}`);
    this.form?.controls[this.fieldName?.toString()].setValue(id);
    this.cd.detectChanges();
  }

  _isRequired(): boolean {
    return (
      this.attribute &&
      this.attribute.validationMessages &&
      this.attribute.validationMessages.find((entry) => entry.type === 'required') != null
    );
  }

  clear(): void {
    //console.log(`Setting value to defaultText: ${this.defaultText}`)
    this.setValue(this.defaultText);
    //console.log(`current form values: `, this.form.value)
  }
}
